import axios from 'axios';
import API from "../config/API";
import siteSetting from "../config/env";
import { toLegacyEnvelope } from "./user.service";

export const hairdresserService = {
    salonProfile,
    getClientsPaginated,
    switchProfile,
    getClientDetail,
    clientAdd,
    addStaff,
    editStaff,
    updateStaffPay,
    hairdresserUserService,
    saveRota,
    editClient,
    removeStaff,
    deleteClient,
    checkEmailAvailability,
    serviceHairdresserList,
    getLastBookedClients,
    staffRotaV2,
    getDiaryData,
    saveDiaryStaffOrder,
    staffListServiceV2WithoutService,
    updateInterval,
    hairdresserProfileWeb,
    getServicesGroupedV2,
    salonServicesWeb,
    getHairdresserPolicies,
    saveWorkingRota,
    clientShare,
    getPaymentMethods,
    updatePaymentMethods,
    // New profile management functions (v2 API)
    getProfileV2,
    updateProfileV2,
    previewBrandSlug,
    revertBrandSlug,
    uploadPortfolioImage,
    deletePortfolioImage,
    // V2 endpoint for the Edit Staff modal (replaces legacy v1 bookingProfile
    // call for that flow). Returns the shape EditStaff already consumes.
    getStaffEditInfoV2,
};


function hairdresserProfileWeb(params) {
    // Use new streamlined v2 API endpoint - no clientId/clientSecret needed
    //console.log('params',params);
    let  headers= {
        'Content-Type' : 'application/json',
        'Authorization': 'Bearer '+localStorage.getItem('accessToken'),
    };
    return axios.get(API.HAIRDRESSER_SERVICES+params.hairdresserId+'/services', {params:params,headers:headers})
}

/**
 * Hits the v2 /hairdresser/:id/edit-info endpoint. Returns the same
 * { data: { userInfo: { ..., hairdresser: { ... } } } } shape as the
 * legacy v1 bookingProfile call so EditStaff can swap one URL for the
 * other without touching its state-mapping logic.
 */
function getStaffEditInfoV2(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': 'Basic YmV1YXBwOmJldWFwcA==',
        'accessToken': `${accessToken}`,
    };
    return axios.get(
        API.HAIRDRESSER_SERVICES + params.hairdresserId + '/edit-info',
        { headers }
    );
}

// DiaryV2 service pickers historically called the V1 dashboard endpoint,
// which reads from the legacy hairdresser.services subdoc and never sees
// the buffer + gate fields written by the v2 service editor. This wrapper
// hits the v2 services endpoint and reshapes the flat services[] into the
// category-grouped { hairdresserSelectedServices: [{ categoryName, hairdresserService: [...] }] }
// shape the picker expects, so existing UI code keeps working.
function getServicesGroupedV2(params) {
    const headers = {
        'Content-Type' : 'application/json',
        'Authorization': 'Bearer '+localStorage.getItem('accessToken'),
    };
    // Multi-location: a person's services are per salon. Staff surfaces
    // (diary Add Service, booking edit, checkout) always mean the salon the
    // user is working in right now, so send it — without it the API answers
    // with every site's services for that person.
    const salonId = params.salonId || localStorage.getItem('salonId') || undefined;
    const query = salonId ? { ...params, salonId } : params;
    return axios.get(API.HAIRDRESSER_SERVICES+params.hairdresserId+'/services', {params: query, headers})
        .then((response) => {
            // v2 wraps the payload as { statusCode, responseData: { ... } }.
            // Tolerate both `responseData` (raw v2) and `data` (already-filtered)
            // shapes so this helper survives a future ResponseFilter refactor.
            const payload = response?.data?.responseData
                || response?.data?.data
                || {};
            const services = Array.isArray(payload.services) ? payload.services : [];
            const buckets = new Map();
            services.forEach((svc) => {
                const cat = svc.category || {};
                const catId = cat._id ? String(cat._id) : '__uncategorised__';
                if (!buckets.has(catId)) {
                    buckets.set(catId, {
                        _id: cat._id || null,
                        categoryName: cat.categoryName || 'Other',
                        orderNumber: cat.orderNumber ?? 9999,
                        // Some consumers read `cat.hairdresserService`, others
                        // (BookingDetailPanel.loadAvailableServicesForStaff)
                        // read `cat.services`. Populate both so the swap can
                        // be transparent.
                        hairdresserService: [],
                        services: [],
                    });
                }
                buckets.get(catId).hairdresserService.push(svc);
                buckets.get(catId).services.push(svc);
            });
            const grouped = Array.from(buckets.values())
                .sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0));
            // Re-shape so the consumer can read response.data.data.hairdresserSelectedServices
            // the way it did on the V1 dashboard endpoint.
            const original = response.data || {};
            const wrappedData = {
                ...payload,
                hairdresserSelectedServices: grouped,
            };
            return {
                ...response,
                data: { ...original, data: wrappedData },
            };
        });
}

function salonServicesWeb(params) {
    // Use new streamlined v2 API endpoint for salon services - no clientId/clientSecret needed
    let  headers= {
        'Content-Type' : 'application/json',
        'Authorization': 'Bearer '+localStorage.getItem('accessToken'),
    };
    return axios.get(API.SALON_SERVICES+params.salonId+'/services', {params:params,headers:headers})
}


function salonProfile(params) {
    //console.log('params',params);
    return axios.get(API.SALON_PROFILE+params.salonId, {params:params})
}


function getClientsPaginated(params) {
    let headers = {
        'Content-Type': 'application/json',
        'accessToken': localStorage.getItem('accessToken'),
    };
    return axios.post(API.HAIRDRESSER_CLIENTS_PAGINATED, params, {headers: headers});
}

// v2: switchTo 0 = client mode, 1 = business (only to the caller's own
// hairdresser record). Reply presented in the v1 envelope the headers read.
function switchProfile(params) {
    return axios.get(API.HAIRDRESSER_SWITCH_PROFILE, {
        params: { switchTo: params.switchTo, latitude: params.latitude, longitude: params.longitude },
        headers: {
            'Content-Type': 'application/json',
            'accessToken': localStorage.getItem('accessToken') || ''
        }
    }).then(toLegacyEnvelope)
}


function getClientDetail(params) {
    let headers = {
        'Content-Type': 'application/json',
        'accessToken': localStorage.getItem('accessToken'),
    };
    const endpoint = API.HAIRDRESSER_SERVICES+'client/details';
    console.log('=== CALLING CLIENT DETAIL API ===');
    console.log('Endpoint:', endpoint);
    console.log('Params:', params);
    console.log('================================');
    return axios.post(endpoint, params, {headers: headers})
        .then(response => {
            console.log('=== CLIENT DETAIL API RESPONSE ===');
            console.log('Response:', response.data);
            console.log('==================================');
            return response;
        });
}


function clientAdd(params) {
    // Use new v2 API endpoint for adding clients
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'accessToken': accessToken,
    };
    return axios.post(API.CLIENT_ADD, params, { headers: headers });
}


function addStaff(params) {
    let accessToken=localStorage.getItem('accessToken')
    let  headers= {
        'Content-Type' : 'multipart/form-data',
        //'Authorization': 'Bearer '+localStorage.getItem('accessToken'),
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    // return axios.post(API.HAIRDRESSER_PROFILE+'addemp',params, {headers:headers})
    return axios.post(API.ADD_STAFF_V2,params, {headers:headers})
}

// function editStaff(params) {
//
//     let  headers= {
//         'Content-Type' : 'multipart/form-data',
//         'Authorization': 'Bearer '+localStorage.getItem('accessToken'),
//     };
//     return axios.post(API.HAIRDRESSER_PROFILE+'editemp',params, {headers:headers})
// }

function editStaff(params) {
    let accessToken=localStorage.getItem('accessToken')
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(API.EDIT_STAFF_V2,params, {headers:headers})
}

function updateStaffPay(params) {
    let accessToken=localStorage.getItem('accessToken')
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(API.UPDATE_STAFF_PAY,params, {headers:headers})
}


// Legacy GET hairdresser/:id/dashboard replaced by the v2 services endpoint
// (getServicesGroupedV2), which already returns the category-grouped
// { hairdresserSelectedServices } shape. v2 service rows carry their id as
// `_id`; legacy rows called it `serviceId`, which the pickers still read.
function hairdresserUserService(params) {
    return getServicesGroupedV2({ hairdresserId: params.hairdresserId })
        .then((response) => {
            const data = (response && response.data && response.data.data) || {};
            (data.hairdresserSelectedServices || []).forEach((cat) => {
                (cat.hairdresserService || []).forEach((svc) => {
                    if (svc && svc.serviceId === undefined) svc.serviceId = svc._id;
                });
            });
            return response;
        });
}


// Team list. Was legacy POST hairdresser/employee/service/v2WithoutService;
// now the v2 hairdresser-staff list (same rows: you first, then the team by
// name; a non-owner without the team permission sees only themselves).
// Callers still read the legacy envelope `response.data.data.total`, so
// keep that shape. v2 answers HTTP 200 on failure, so reject on statusCode.
function staffListServiceV2WithoutService() {
    const headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': localStorage.getItem('accessToken'),
    };
    return axios.post(API.STAFF_LIST_V2, {}, { headers })
        .then((response) => {
            const payload = (response && response.data) || {};
            if (payload.statusCode !== 1) {
                const err = new Error((payload.error && payload.error.responseMessage) || 'Unable to load staff');
                err.response = response;
                throw err;
            }
            const result = payload.responseData && payload.responseData.result;
            const total = (Array.isArray(result) ? result : []).filter(Boolean);
            return { ...response, data: { errorCode: 0, data: { total } } };
        });
}


function saveDiaryStaffOrder(staffOrder) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(API.DIARY_STAFF_ORDER, { staffOrder }, { headers });
}

function getDiaryData(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.get(API.DIARY_DATA, { params, headers });
}

function staffRotaV2(params) {
    // params.deviceToken='sdgfsdgsd';
    // let  headers= {
    //     'Content-Type' : 'application/json',
    //     'Authorization': 'Bearer '+localStorage.getItem('accessToken'),
    // };
    // return axios.get(API.HAIRDRESSER_PROFILE+params.hairdresserId+'/rota/emp/v2', {params:params,headers:headers})

    let accessToken=localStorage.getItem('accessToken')
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(API.STAFF_LIST_V2,params, {headers:headers})
}


function saveRota(params) {
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': localStorage.getItem('accessToken')
    };
    return axios.put(API.HAIRDRESSER_ROTA_SAVE_V2+params.hairdresserId+'/rota', params, {headers:headers})
        .then(adaptV2RotaResponse)
}

function editClient(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'accessToken': accessToken,
    };
    
    // Use new v2 API endpoint
    // The new API expects clientId in body and other fields as updates
    const { userId, hairdresserId, ...updates } = params;
    
    return axios.post(API.CLIENT_EDIT, {
        clientId: userId,
        ...updates
    }, { headers: headers });
}

// function removeStaff(params) {
//     let  headers= {
//         'Content-Type' : 'application/json',
//         'Authorization': 'Bearer '+localStorage.getItem('accessToken'),
//     };
//     return axios.post(API.HAIRDRESSER_PROFILE+'empremove',params, {headers:headers})
// }

function removeStaff(params) {
    let accessToken=localStorage.getItem('accessToken')
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(API.REMOVE_STAFF_V2,params, {headers:headers})
}

function deleteClient(params) {
    // v2. The old v1 route (hairdresser/:id/client/remove) only pushed to the
    // legacy removeClient array, which MIGRATED salons don't read — their
    // client list comes from salonclients — so deleting a client silently did
    // nothing for them (Equilibrium, 2026-08-20). The v2 endpoint goes through
    // clientStorageHelper and writes to whichever storage the salon is on.
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': localStorage.getItem('accessToken')
    };
    return axios.post(API.HAIRDRESSER_CLIENT_REMOVE, { clientId: params.userId }, { headers: headers })
}

function checkEmailAvailability(params) {
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA=="
    };
    return axios.post(API.CHECK_EMAIL, params,{headers:headers})
}

function serviceHairdresserList(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(API.SERVICE_HAIRDRESSER_LIST_V2, params, {headers: headers})
}


function getHairdresserPolicies(params) {
    let accessToken=localStorage.getItem('accessToken')
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(API.GET_HAIRDRESSER_POLICIES_V2,params, {headers:headers})

    
}


function getLastBookedClients(params) {
    let accessToken=localStorage.getItem('accessToken')
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(API.LAST_BOOKED_CLIENTS,params, {headers:headers})
}

function updateInterval(params) {
    let accessToken=localStorage.getItem('accessToken')
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(API.HAIRDRESSER_UPDATE_INTERVAL,params, {headers:headers})
}

function saveWorkingRota(params) {
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': localStorage.getItem('accessToken')
    };
    return axios.post(API.HAIRDRESSER_WORKING_ROTA_SAVE_V2+params.hairdresserId+'/working-rota', params, {headers:headers})
        .then(adaptV2RotaResponse)
}

// Staff.js still consumes the legacy `{ errorCode, messages }` response shape.
// Keep that UI contract while moving persistence to v2; the transport envelope
// remains available under `v2Response` for any newer caller that needs it.
function adaptV2RotaResponse(response) {
    const payload = response && response.data ? response.data : {};
    if (payload.statusCode === 1) {
        return {
            ...response,
            data: { errorCode: 0, ...(payload.responseData || {}), v2Response: payload }
        };
    }
    const error = payload.error || {};
    return {
        ...response,
        data: {
            errorCode: error.errorCode || payload.statusCode || 1,
            messages: error.message || error.responseMessage || 'Unable to save rota.',
            v2Response: payload
        }
    };
}

function clientShare(params) {
    let accessToken=localStorage.getItem('accessToken')
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(API.CLIENT_SHARE,params, {headers:headers})
}

function getPaymentMethods(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.get(API.PAYMENT_METHODS_GET, { params, headers });
}

function updatePaymentMethods(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    return axios.post(API.PAYMENT_METHODS_UPDATE, params, { headers });
}

// ==================== New Profile Management Functions (v2 API) ====================

/**
 * Get hairdresser profile data from v2 API
 * @returns {Promise} Profile data including bio, portfolio, and user info
 */
function getProfileV2() {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'accessToken': accessToken
    };
    return axios.get(API.HAIRDRESSER_PROFILE_GET, { headers });
}

/**
 * Update hairdresser profile data via v2 API
 * @param {Object} params - Profile data to update
 * @param {string} [params.shortBio] - Bio text
 * @param {string} [params.jobTitle] - Job title
 * @param {string} [params.first_name] - First name
 * @param {string} [params.last_name] - Last name
 * @param {string} [params.email] - Email
 * @param {string} [params.phoneNumber] - Phone number
 * @param {string} [params.currency] - Currency code
 * @param {string} [params.currencySymbol] - Currency symbol
 * @param {string} [params.currencyCountry] - Currency country code
 * @param {number} [params.hairdresserBookingInterval] - Booking interval in minutes
 * @returns {Promise} Update result
 */
function previewBrandSlug(tradingName) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = { 'accessToken': accessToken };
    return axios.get(API.HAIRDRESSER_PROFILE_UPDATE + '/brand-slug-preview', { headers, params: { tradingName } });
}

function revertBrandSlug() {
    let accessToken = localStorage.getItem('accessToken');
    let headers = { 'accessToken': accessToken };
    return axios.post(API.HAIRDRESSER_PROFILE_UPDATE + '/brand-slug-revert', {}, { headers });
}

function updateProfileV2(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'accessToken': accessToken
    };
    return axios.put(API.HAIRDRESSER_PROFILE_UPDATE, params, { headers });
}

/**
 * Upload a portfolio image
 * @param {File} imageFile - Image file to upload
 * @returns {Promise} Upload result with image URL
 */
function uploadPortfolioImage(imageFile) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'multipart/form-data',
        'accessToken': accessToken
    };
    
    const formData = new FormData();
    formData.append('image', imageFile);
    
    return axios.post(API.HAIRDRESSER_PORTFOLIO_UPLOAD, formData, { headers });
}

/**
 * Delete a portfolio image
 * @param {string} imageId - Portfolio image ID to delete
 * @returns {Promise} Delete result
 */
function deletePortfolioImage(imageId) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'accessToken': accessToken
    };
    return axios.delete(`${API.HAIRDRESSER_PORTFOLIO_DELETE}/${imageId}`, { headers });
}
